import React, { useState, useEffect } from "react";
import {
    Plus,
    Edit,
    Trash2,
    Save,
    X,
    Trophy,
    Calendar,
    Award,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import type { Achievement } from "../../lib/supabase";
import { ImageUpload } from "./ImageUpload";
import { useSimpleProfileData } from "../../hooks/useProfileData";

interface AchievementFormData {
    title: string;
    description: string;
    achievement_date: string;
    category: string;
    image_url: string;
    level: string;
}

const initialFormData: AchievementFormData = {
    title: "",
    description: "",
    achievement_date: "",
    category: "",
    image_url: "",
    level: "Sekolah",
};

export const AchievementsManagement: React.FC = () => {
    const [achievements, setAchievements] = useState<Achievement[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { profileData } = useSimpleProfileData();
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formData, setFormData] =
        useState<AchievementFormData>(initialFormData);

    useEffect(() => {
        fetchAchievements();
    }, []);

    const fetchAchievements = async () => {
        try {
            setLoading(true);
            setError(null);

            const { data, error } = await supabase
                .from("achievements")
                .select("*")
                .order("achievement_date", { ascending: false });

            if (error) throw error;
            setAchievements(data || []);
        } catch (err) {
            console.error("Error fetching achievements:", err);
            setError("Gagal memuat data pencapaian");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (
        field: keyof AchievementFormData,
        value: string
    ) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };



    // Function to sync existing achievement images to gallery
    const syncExistingImagesToGallery = async () => {
        if (!profileData?.id) return;

        setSaving(true);
        setError(null);

        try {
            // Get all achievements with images
            const { data: achievementsWithImages, error: fetchError } =
                await supabase
                    .from("achievements")
                    .select("id, title, description, image_url")
                    .eq("profile_id", profileData.id)
                    .not("image_url", "is", null)
                    .neq("image_url", "");

            if (fetchError) throw fetchError;

            if (
                !achievementsWithImages ||
                achievementsWithImages.length === 0
            ) {
                setSuccess(
                    "Tidak ada gambar achievement yang perlu disinkronkan."
                );
                setTimeout(() => setSuccess(null), 3000);
                return;
            }

            // Add each image to gallery
            let syncedCount = 0;
            for (const achievement of achievementsWithImages) {
                const wasAdded = await addImageToGalleryWithCheck(
                    achievement.image_url,
                    achievement.title,
                    achievement.description,
                    profileData.id
                );
                if (wasAdded) syncedCount++;
            }

            setSuccess(
                `Berhasil menyinkronkan ${syncedCount} gambar achievement ke galeri!`
            );
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error("Error syncing images to gallery:", err);
            setError("Gagal menyinkronkan gambar ke galeri");
        } finally {
            setSaving(false);
        }
    };

    // Helper function that returns whether image was actually added
    const addImageToGalleryWithCheck = async (
        imageUrl: string,
        title: string,
        description: string,
        profileId: string
    ): Promise<boolean> => {
        if (!imageUrl) return false;

        try {
            // Check if image already exists in gallery
            const { data: existingGallery } = await supabase
                .from("gallery")
                .select("id")
                .eq("image_url", imageUrl)
                .eq("profile_id", profileId)
                .single();

            if (existingGallery) {
                return false; // Already exists
            }

            const galleryData = {
                title: `${title}`,
                description: description,
                image_url: imageUrl,
                profile_id: profileId,
                sort_order: 0,
            };

            const { error } = await supabase
                .from("gallery")
                .insert([galleryData]);

            if (error) {
                console.error("Error adding image to gallery:", error);
                return false;
            }

            return true; // Successfully added
        } catch (err) {
            console.error("Error adding image to gallery:", err);
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const achievementData = {
                title: formData.title,
                description: formData.description,
                achievement_date: formData.achievement_date,
                category: formData.category,
                image_url: formData.image_url,
                level: formData.level,
                profile_id: profileData?.id,
            };

            if (editingId) {
                // Update existing achievement
                const { error } = await supabase
                    .from("achievements")
                    .update(achievementData)
                    .eq("id", editingId);

                if (error) throw error;

                // Add image to gallery if image_url exists (using the function that checks for duplicates)
                if (formData.image_url && profileData?.id) {
                    const wasAdded = await addImageToGalleryWithCheck(
                        formData.image_url,
                        formData.title,
                        formData.description,
                        profileData.id
                    );
                    
                    if (wasAdded) {
                        console.log("Image successfully added to gallery");
                    }
                }

                setSuccess("Pencapaian berhasil diperbarui!");
            } else {
                // Create new achievement
                const { error } = await supabase
                    .from("achievements")
                    .insert([achievementData]);

                if (error) throw error;

                // Add image to gallery if image_url exists (using the function that checks for duplicates)
                if (formData.image_url && profileData?.id) {
                    const wasAdded = await addImageToGalleryWithCheck(
                        formData.image_url,
                        formData.title,
                        formData.description,
                        profileData.id
                    );
                    
                    if (wasAdded) {
                        console.log("Image successfully added to gallery");
                    }
                }

                setSuccess("Pencapaian berhasil ditambahkan!");
            }

            setFormData(initialFormData);
            setShowForm(false);
            setEditingId(null);
            fetchAchievements();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error("Error saving achievement:", err);
            setError("Gagal menyimpan pencapaian");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (achievement: Achievement) => {
        setFormData({
            title: achievement.title,
            description: achievement.description,
            achievement_date: achievement.achievement_date,
            category: achievement.category,
            image_url: achievement.image_url || "",
            level: achievement.level || "Sekolah",
        });
        setEditingId(achievement.id);
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Apakah Anda yakin ingin menghapus pencapaian ini?")) {
            return;
        }

        try {
            setError(null);
            const { error } = await supabase
                .from("achievements")
                .delete()
                .eq("id", id);

            if (error) throw error;

            setSuccess("Pencapaian berhasil dihapus!");
            fetchAchievements();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error("Error deleting achievement:", err);
            setError("Gagal menghapus pencapaian");
        }
    };

    const handleCancel = () => {
        setFormData(initialFormData);
        setShowForm(false);
        setEditingId(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                            Kelola Pencapaian
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Tambah, edit, atau hapus pencapaian yang akan
                            ditampilkan di portofolio
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                        <button
                            onClick={syncExistingImagesToGallery}
                            disabled={saving}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                                    Menyinkronkan...
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="h-4 w-4 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                        />
                                    </svg>
                                    Sinkron ke Galeri
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => setShowForm(true)}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Tambah Pencapaian
                        </button>
                    </div>
                </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="text-sm text-red-700">{error}</div>
                </div>
            )}

            {success && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="text-sm text-green-700">{success}</div>
                </div>
            )}

            {/* Form Modal */}
            {showForm && (
                <div className="mb-6 bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                            {editingId
                                ? "Edit Pencapaian"
                                : "Tambah Pencapaian Baru"}
                        </h3>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="title"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Judul Pencapaian *
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    required
                                    value={formData.title}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "title",
                                            e.target.value
                                        )
                                    }
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Masukkan judul pencapaian"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="category"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Kategori *
                                </label>
                                <input
                                    type="text"
                                    id="category"
                                    required
                                    value={formData.category}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "category",
                                            e.target.value
                                        )
                                    }
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Contoh: Akademik, Olahraga, Seni"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="level"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Level Prestasi *
                                </label>
                                <select
                                    id="level"
                                    required
                                    value={formData.level}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "level",
                                            e.target.value
                                        )
                                    }
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="Sekolah">Sekolah</option>
                                    <option value="Kabupaten">Kabupaten</option>
                                    <option value="Kota">Kota</option>
                                    <option value="Provinsi">Provinsi</option>
                                    <option value="Nasional">Nasional</option>
                                    <option value="Event Organizer">
                                        Event Organizer
                                    </option>
                                </select>
                            </div>

                            <div></div>
                        </div>

                        <div>
                            <label
                                htmlFor="description"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                Deskripsi *
                            </label>
                            <textarea
                                id="description"
                                required
                                rows={3}
                                value={formData.description}
                                onChange={(e) =>
                                    handleInputChange(
                                        "description",
                                        e.target.value
                                    )
                                }
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Jelaskan pencapaian ini..."
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="achievement_date"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Tanggal *
                                </label>
                                <input
                                    type="date"
                                    id="achievement_date"
                                    required
                                    value={formData.achievement_date}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "achievement_date",
                                            e.target.value
                                        )
                                    }
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Gambar Pencapaian
                                </label>
                                <ImageUpload
                                    value={formData.image_url}
                                    onChange={(url) =>
                                        handleInputChange("image_url", url)
                                    }
                                    bucket="portfolio-images"
                                    folder="achievements"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                <X className="h-4 w-4 mr-2" />
                                Batal
                            </button>
                            <button
                                type="submit"
                                disabled={
                                    saving ||
                                    !formData.title ||
                                    !formData.description ||
                                    !formData.achievement_date ||
                                    !formData.category ||
                                    !formData.level
                                }
                                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Menyimpan...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        {editingId ? "Perbarui" : "Simpan"}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Achievements List */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        Daftar Pencapaian ({achievements.length})
                    </h3>
                </div>

                {achievements.length === 0 ? (
                    <div className="p-6 text-center">
                        <Trophy className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                            Belum ada pencapaian
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Mulai dengan menambahkan pencapaian pertama.
                        </p>
                        <div className="mt-6">
                            <button
                                onClick={() => setShowForm(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Tambah Pencapaian
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Pencapaian
                                        </th>
                                        <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Kategori
                                        </th>
                                        <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Level
                                        </th>
                                        <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Tanggal
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Aksi
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {achievements.map((achievement) => (
                                        <tr
                                            key={achievement.id}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-4 sm:px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="relative group mr-3 sm:mr-4 flex-shrink-0">
                                                        {achievement.image_url ? (
                                                            <img
                                                                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover"
                                                                src={
                                                                    achievement.image_url
                                                                }
                                                                alt={
                                                                    achievement.title
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                                                                <Award className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                                            </div>
                                                        )}
                                                        {/* Hover overlay with text */}
                                                        <div className="absolute inset-0 bg-black bg-opacity-75 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center items-center p-1">
                                                            <p className="text-white text-xs font-medium text-center leading-tight truncate w-full">
                                                                {achievement.title}
                                                            </p>
                                                            <p className="text-white text-xs text-center leading-tight truncate w-full mt-0.5">
                                                                {achievement.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-gray-900 truncate">
                                                            {achievement.title}
                                                        </div>
                                                        <div className="text-xs sm:text-sm text-gray-500 truncate">
                                                            {
                                                                achievement.description
                                                            }
                                                        </div>
                                                        <div className="sm:hidden mt-1 flex flex-wrap gap-1">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                {
                                                                    achievement.category
                                                                }
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                {new Date(
                                                                    achievement.achievement_date
                                                                ).toLocaleDateString(
                                                                    "id-ID"
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {achievement.category}
                                                </span>
                                            </td>
                                            <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    {achievement.level ||
                                                        "Tidak ada"}
                                                </span>
                                            </td>
                                            <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    <Calendar className="h-4 w-4 mr-1" />
                                                    {new Date(
                                                        achievement.achievement_date
                                                    ).toLocaleDateString(
                                                        "id-ID"
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-1 sm:space-x-2">
                                                    <button
                                                        onClick={() =>
                                                            handleEdit(
                                                                achievement
                                                            )
                                                        }
                                                        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(
                                                                achievement.id
                                                            )
                                                        }
                                                        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                                                        title="Hapus"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
